import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Grid from '@mui/material/Grid2'
import { useTranslation } from 'react-i18next'
import { IPersonRole, IProject } from 'types/cvsInterfaces'
import { IItemVisibility, ILayout } from 'types/layoutInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import { convertToDisplayDate } from 'utils/utils'
import VisibilityCheckbox from '../VisibilityCheckbox'
import HideDetailsSettings from './HideDetailsSettings'
import { findHideSetting, hasHiddenDetails } from '../hideSettings'
import colors from 'constants/colors'

interface ProjectsProps {
  projects: IProject[]
  layout: ILayout
  hideSection: (value: boolean, type: string) => void
  hideItem: (value: boolean, id: number, type: string) => void
  expanded: boolean
  setStatus: React.Dispatch<React.SetStateAction<boolean>>
  handleHideSettings: (data: Record<string, boolean>, type: string) => void
}

/**
 * Renders a list of projects in an accordion component.
 *
 * @param {Object[]} projects - The array of project objects to render.
 * @param {string} layout - The layout configuration for the projects.
 * @param {function} hideSection - The function to hide or show the projects section.
 * @param {function} hideItem - The function to hide or show an individual project item.
 * @param {boolean} expanded - Whether the accordion is expanded or not.
 * @param {function} setStatus - The function to set the status of the accordion.
 * @return {React.ReactElement} The rendered projects component.
 * @notExported
 */
const Projects: React.FC<ProjectsProps> = ({
  projects,
  layout,
  hideSection,
  hideItem,
  expanded,
  setStatus,
  handleHideSettings,
}) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  const hideSetting = findHideSetting(layout, 'projects')

  let hidden = false
  if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
    const visibilities = layout.ItemVisibilities.filter((visibility: IItemVisibility) => visibility.type === 'projects')
    if (visibilities.length > 0) {
      const visibilityMap = new Map(visibilities.map(item => [item.itemId, item.hidden]))
      hidden = projects.every(value => visibilityMap.get(value.id) === true)
    }
  }

  const Item: React.FC<{ project: IProject }> = ({ project }) => {
    let hiddenItem = false
    if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
      const visibilities = layout.ItemVisibilities.filter(
        (visibility: IItemVisibility) => visibility.type === 'projects'
      )
      if (visibilities.length > 0) {
        hiddenItem = visibilities.find((value: IItemVisibility) => value.itemId === project.id && value.hidden === true)
          ? true
          : false
      }
    }

    return (
      <Box
        display="flex"
        flexDirection="row"
        flexWrap="nowrap"
        width="100%"
        my={2}
        sx={{
          padding: '2px',
          backgroundColor: theme.palette.background.default,
          '&:hover': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: '5px',
          },
        }}
      >
        <Box width="100%">
          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <VisibilityCheckbox
              checked={!!hiddenItem}
              onChange={(checked: boolean) => hideItem(checked, project.id, 'projects')}
            />
            <Box fontWeight="fontWeightBold" flexGrow={1}>
              {chooseDBTranslation(i18n, project).name}
            </Box>
            <Box justifyContent="flex-end">
              {convertToDisplayDate(project.startDate, project.dateResolution)} -{' '}
              {project.endDate ? convertToDisplayDate(project.endDate, project.dateResolution) : null}
            </Box>
          </Box>
          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <Box mr={1} fontWeight="fontWeightBold">
              {t('print.project.roles')}:
            </Box>
            <Box display="inline">
              {chooseDBTranslation(i18n, project).primaryRole.length > 0
                ? chooseDBTranslation(i18n, project).primaryRole
                : project.ProjectRoles?.map((role: IPersonRole) => `${chooseDBTranslation(i18n, role.Skill).name} `)}
            </Box>
          </Box>
          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <Box mr={1} fontWeight="fontWeightBold">
              {t('print.project.customer')}:
            </Box>
            <Box display="inline">{chooseDBTranslation(i18n, project).customerName}</Box>
          </Box>
          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <Box mr={1} fontWeight="fontWeightBold">
              {t('print.project.employer')}:
            </Box>
            <Box display="inline">{chooseDBTranslation(i18n, project).employerName}</Box>
          </Box>
          <Box>{chooseDBTranslation(i18n, project).description}</Box>
        </Box>
      </Box>
    )
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, expanded) => setStatus(expanded)}
      elevation={0}
      sx={{ borderRadius: '5px', border: `1px solid ${colors.borderColor}` }}
      slotProps={{
        transition: { unmountOnExit: true },
      }}
    >
      <Box display="flex" alignItems="center">
        <VisibilityCheckbox checked={hidden} onChange={(checked: boolean) => hideSection(checked, 'projects')} />
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flex: 1,
            fontSize: '20px',
            color: theme.palette.primary.main,
            fontWeight: 'bold',
          }}
        >
          <Grid container alignItems="center">
            <Grid>{t('print.layout.projects')}</Grid>
            <Grid sx={{ marginLeft: 1, fontSize: '11pt', color: theme.palette.text.secondary, fontWeight: 'normal' }}>
              {hasHiddenDetails(hideSetting, 'projects')
                ? t('print.hideSetting.enabled')
                : t('print.hideSetting.disabled')}
            </Grid>
          </Grid>
        </AccordionSummary>
      </Box>
      <AccordionDetails>
        <HideDetailsSettings type="projects" layout={layout} handleHideSettings={handleHideSettings} />
        <Box display="flex" flexDirection="column" flexWrap="wrap" width="100%">
          {projects.map((project: IProject, index: number) => {
            return (
              <Box key={`project-${project.id}`}>
                <Item project={project} />
                {index + 1 < projects.length ? <Divider variant="fullWidth" /> : null}
              </Box>
            )
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default Projects
